import { PureComponent, ReactNode, useEffect } from 'react';
import Select, {
    components,
    ControlProps,
    IndicatorProps,
    MenuProps,
    StylesConfig,
} from 'react-select';
import { CSSObject } from '@emotion/serialize';
import { getUnit } from '../../store/utils';
import { DAYS, MONTHS, YEARS } from './constants/add-patient';
import { isAbhaV2 } from '../../pages/ToggleAbhaFlowVersion';

const CREATE_UNITS: {
    id: YEARS | MONTHS | DAYS;
    label: string;
    getValue: (p: number) => number;
    max?: number;
}[] = [
    { id: 'YEARS', label: 'Years', getValue: (p: number) => p * 12 },
    { id: 'MONTHS', label: 'Months', getValue: (p: number) => p },
    // It's exception, deals with days
    { id: 'DAYS', label: 'Days', getValue: (p: number) => p },
];

type Item = {
    id: YEARS | MONTHS | DAYS;
    label: string;
    value: number;
};

type State = {
    options: Item[];
};

type Props = {
    onSelect: (item: Item | null) => void;
    value: Item | null;
    isModal?: boolean;
    disabled?: boolean;
};

const DropdownIndicator = (props: IndicatorProps<Item, false>) => {
    const { selectProps } = props;
    const { value } = selectProps;
    return !value ? (
        <components.IndicatorsContainer {...props}>
            <svg
                width="20"
                height="20"
                viewBox="0 0 28 28"
                className="cursor-pointer downarrow fill-neutral-5"
                preserveAspectRatio="xMidYMid meet"
            >
                <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"></path>
            </svg>
        </components.IndicatorsContainer>
    ) : null;
};

const ClearIndicator = (props: IndicatorProps<Item, false>) => {
    return (
        <components.ClearIndicator {...props}>
            <svg
                width="16"
                height="16"
                viewBox="0 0 28 28"
                className="cursor-pointer close fill-neutral-5"
                preserveAspectRatio="xMidYMid meet"
            >
                <path d="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z"></path>
            </svg>
        </components.ClearIndicator>
    );
};

const IndicatorSeparator = () => null;
const NoOptionsMessage = () => null;

const Control = (props: ControlProps<Item, false>) => {
    const { getValue } = props;

    return (
        <components.Control
            {...props}
            className={`flex border py-1 ${
                getValue() && getValue().length > 0 ? 'border-neutral-black' : 'border-neutral-6'
            } ${props.menuIsOpen ? 'text-neutral-4' : ''} ${
                !props.menuIsOpen && getValue() && getValue().length > 0 ? 'font-700' : ''
            } focus-within:border-neutral-1 rounded-4`}
        >
            {props.children}
        </components.Control>
    );
};

const Menu = (props: MenuProps<Item, false>) => {
    const { options } = props;
    if (options.length === 0) {
        return null;
    }
    return <components.Menu {...props}>{props.children}</components.Menu>;
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const Input = (props) => {
    useEffect(() => {
        if (props.selectProps.inputType) {
            const inEl = document.getElementById(props.id) as HTMLInputElement;

            if (inEl) {
                inEl.setAttribute('type', props.selectProps.inputType);
            }
        }
    }, []);

    return <components.Input {...props} />;
};

const customComponents = {
    ClearIndicator,
    DropdownIndicator,
    IndicatorSeparator,
    NoOptionsMessage,
    Menu,
    Control,
    Input,
};

const customStyles: (isModal?: boolean) => StylesConfig<Item, false> = (isModal?: boolean) => {
    if (isModal) {
        return {
            control: () => {
                return { padding: 3, borderWidth: 0 } as CSSObject;
            },
        };
    }

    return {
        control: () => {
            return { padding: 3 } as CSSObject;
        },
    };
};

class NewPatientAgeInput extends PureComponent<Props, State> {
    state: State = {
        options: [],
    };

    onInputChange = (newValue: string): void => {
        this.setState({
            options: generateOptions(newValue),
        });
    };

    value: Item | null = null;

    onSelect = (value: Item | null): void => {
        this.value = value;
        this.setState({
            options: value ? generateOptions(value.label) : [],
        });

        this.props.onSelect(value);
    };

    private readonly filterOption = () => true;

    isOptionSelected = (option: Item | null): boolean => {
        return (
            option?.id === this.props.value?.id &&
            option?.label === this.props.value?.label &&
            option?.value === this.props.value?.value
        );
    };

    render(): ReactNode {
        const { options } = this.state;
        const { value, disabled } = this.props;
        return (
            <Select
                value={value}
                isClearable
                options={options}
                filterOption={this.filterOption}
                isOptionSelected={this.isOptionSelected}
                components={customComponents}
                styles={customStyles(this.props.isModal)}
                onInputChange={this.onInputChange}
                placeholder={
                    this.props.isModal || isAbhaV2() ? 'Age' : 'e.g. 4 Days, 10 Years, 11 Months'
                }
                onChange={this.onSelect}
                inputType={'number'}
                tabSelectsValue={false}
                className={this.props.isModal ? 'bg-darwin-neutral-50 w-50p' : ''}
                isDisabled={disabled}
                // onMenuClose={this.onBlur}
            />
        );
    }
}

export default NewPatientAgeInput;

function generateOptions(newValue: string): Item[] {
    const t = newValue.trim();

    if (!t) {
        return [];
    }

    const n = t.split(' ');

    const pi = parseInt(n[0]);

    if (isNaN(pi)) {
        return [];
    }

    return CREATE_UNITS.filter((c) => !c.max || pi <= c.max).map((c) => {
        const updatedPi = c.id === 'YEARS' ? parseFloat(n[0]) : pi;
        return {
            id: c.id,
            label: `${updatedPi} ${getUnit(c.label, updatedPi.toString())}`,
            value: c.id === 'YEARS' ? Math.floor(c.getValue(updatedPi)) : c.getValue(updatedPi),
        };
    });
}
